import axios from 'axios';
import { notification } from 'antd';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URI,
});

// Human labels for entitlement limit keys (backend entitlements/constants.py).
const LIMIT_LABELS = {
  clusters: 'clusters',
  cloud_accounts: 'cloud accounts',
  projects: 'projects',
  environments: 'environments',
  applications: 'applications',
  users: 'users',
  ai_actions: 'AI actions',
};

// One consistent upgrade prompt for every entitlement denial the backend
// raises (QuotaExceeded → 402 quota_exceeded, FeatureNotInPlan → 403
// feature_locked), instead of per-flow error handling. Personal-plan users
// hit these first (e.g. the 1-cluster limit).
function notifyEntitlementDenial(errorDetails) {
  const detail = errorDetails || {};
  if (detail.code === 'quota_exceeded') {
    const label = LIMIT_LABELS[detail.resource] || detail.resource;
    notification.warning({
      message: 'Plan limit reached',
      description: `You've reached your plan's limit for ${label} (${detail.used}/${detail.limit}). Upgrade your plan to add more.`,
      duration: 6,
    });
    return true;
  }
  if (detail.code === 'feature_locked') {
    notification.warning({
      message: 'Feature not in your plan',
      description: `"${detail.feature}" isn't available on your current plan. Upgrade to unlock it.`,
      duration: 6,
    });
    return true;
  }
  return false;
}

// Add request interceptor
api.interceptors.request.use(
  (config) => {
    console.log('inside interceptors use...');
    // Modify the request config, such as adding headers or authentication tokens
    const accessToken = localStorage.getItem('isAuth');
    if (accessToken) {
      // Sandbox guests (localStorage 'sandbox-guest') hold a real JWT and make
      // real API calls — the old dummy-data "Try without login" reject is gone.
      config.headers['Authorization'] = `Bearer ${accessToken}`;
    }
    else{
      let myVersion = localStorage.getItem("appVersion");
      localStorage.clear();
      localStorage.setItem("appVersion", myVersion);
      window.open("/signin", "_self");
      return Promise.reject("Authorization Failed");
    }
    return config;
  },
  (error) => {
    console.log('Found error in interceptors.request...', error);
    // Handle request errors
    return Promise.reject(error);
  }
);

// Add response interceptor
api.interceptors.response.use(
  (response) => {
    // Process successful responses
    console.log('SUCCESS in interceptors.response...');
    return response;
  },
  async (error) => {
    // Handle response errors
    const originalRequest = error.config;
    console.log('originalRequest in interceptors.response...');

    // Check if the response status is 401 (Unauthorized)
    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      console.log('response status is 401 in interceptors.response...');

      // Sandbox guest sessions can't refresh (no refresh token). When the
      // guest JWT expires, end the tour cleanly: clear storage and land on
      // the sandbox entry's "Session ended" screen.
      if (localStorage.getItem('sandbox-guest') === '1') {
        let myVersion = localStorage.getItem('appVersion');
        localStorage.clear();
        if (myVersion) localStorage.setItem('appVersion', myVersion);
        window.open('/sandbox?expired=1', '_self');
        return Promise.reject(error);
      }

      // Perform a request to refresh the access token
      const refreshToken = localStorage.getItem('refreshToken');
      const rememberMe = localStorage.getItem('iam');
      if (refreshToken) {
        try {
          const response = await axios.post(process.env.REACT_APP_API_URI+'/login/refresh/', { refresh: refreshToken });
          // console.log('calling /login/refresh/ in interceptors.response...:', response.data);
          console.log('calling /login/refresh/ in interceptors.response...');
          // Update the access token in storage
          const newAccessToken = response.data.data.access;
          // const newRefreshToken = response.data.data.refresh;
          // console.log('newAccessToken Found in interceptors.response...', newAccessToken);
          console.log('newAccessToken Found in interceptors.response...');
          localStorage.setItem('isAuth', newAccessToken);
          // localStorage.setItem('refreshToken', newRefreshToken);

          // Update the request configuration with the new access token
          originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;

          // Retry the original request
          return api(originalRequest);
        } catch (refreshError) {
            // Handle the refresh token request error
            console.log('Refresh Token Failed in interceptors.response :', refreshError);
            // redirect to login after clearing localStorage if refresh token failed
            let myVersion = localStorage.getItem("appVersion");
            localStorage.clear();
            localStorage.setItem("appVersion", myVersion);
            window.open("/signin", "_self");
        }
      }
    }
    // Sandbox write attempts (403 code=sandbox_read_only) get one friendly
    // toast instead of generic error handling. The antd `key` dedupes bursts
    // of parallel blocked calls into a single visible notification. The error
    // still propagates so callers stop normally.
    if (error.response && error.response.status === 403) {
      try {
        const body = error.response.data || {};
        const sandboxCode = body.code || body.error_details?.code || body.data?.code;
        if (sandboxCode === 'sandbox_read_only') {
          notification.info({
            key: 'sandbox-read-only',
            message: 'Sandbox cluster is read-only',
            description: body.detail || body.error_details?.detail || 'This is the sandbox cluster — import your own cluster to do this.',
            duration: 5,
          });
          return Promise.reject(error);
        }
        // Guest sandbox sessions are read-only everywhere: same single
        // friendly toast pattern, nudging towards signup instead of import.
        if (sandboxCode === 'sandbox_guest_read_only') {
          notification.info({
            key: 'sandbox-guest-read-only',
            message: 'Sandbox is read-only',
            description: body.detail || body.error_details?.detail || 'Sign up to use Atmosly with your own clusters.',
            duration: 5,
          });
          return Promise.reject(error);
        }
      } catch (e) {
        console.log('sandbox read-only notify failed:', e);
      }
    }
    // Entitlement denials (plan limits / locked features) get a global
    // upgrade prompt; the error still propagates so flows stop normally.
    if (error.response && [402, 403].includes(error.response.status)) {
      try {
        notifyEntitlementDenial(error.response.data?.error_details);
      } catch (e) {
        console.log('entitlement denial notify failed:', e);
      }
    }
    // Handle other response errors
    return Promise.reject(error);
  }
);

export default api;
